import type { ReactNode } from 'react';

export type ExerciseQuestionType = 'QCM' | 'VRAI_FAUX' | 'TEXTE_LIBRE' | 'CLASSEMENT';

export type ExerciseQuestion = {
  id: string;
  type: ExerciseQuestionType;
  libelle: string;
  points?: number;
  options?: string[];
  reponseCorrecte?: string;
  reponseVraiFaux?: boolean;
  reponseTexte?: string;
  items?: string[];
  ordreCorrect?: string[];
};

export const EXERCISE_QUESTION_TYPE_LABELS: Record<ExerciseQuestionType, string> = {
  QCM: 'Choix multiple',
  VRAI_FAUX: 'Vrai / Faux',
  TEXTE_LIBRE: 'Réponse libre',
  CLASSEMENT: 'Classement (glisser-déposer)',
};

type Props = {
  value: ExerciseQuestion[];
  onChange: (questions: ExerciseQuestion[]) => void;
};

const fieldClass =
  'w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

function FieldLabel({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <span className="formateur-notes-field__label" {...(htmlFor ? { id: `${htmlFor}-label` } : {})}>
      {children}
    </span>
  );
}

function LabeledField({
  label,
  htmlFor,
  children,
  className = '',
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={`flex flex-col gap-1 ${className}`.trim()}>
      <FieldLabel htmlFor={htmlFor}>{label}</FieldLabel>
      {children}
    </label>
  );
}

export function newExerciseQuestion(index: number, type: ExerciseQuestionType = 'QCM'): ExerciseQuestion {
  const id = `q${index + 1}`;
  if (type === 'VRAI_FAUX') return { id, type, libelle: '', points: 1, reponseVraiFaux: true };
  if (type === 'TEXTE_LIBRE') return { id, type, libelle: '', points: 1, reponseTexte: '' };
  if (type === 'CLASSEMENT') {
    const items = ['Premier', 'Deuxième', 'Troisième'];
    return { id, type, libelle: '', points: 1, items, ordreCorrect: [...items] };
  }
  return { id, type: 'QCM', libelle: '', points: 1, options: ['A', 'B', 'C'], reponseCorrecte: 'A' };
}

function RankingEditor({
  items,
  onChange,
}: {
  items: string[];
  onChange: (items: string[]) => void;
}) {
  let dragIndex: number | null = null;

  function reorder(from: number, to: number) {
    if (from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  }

  return (
    <div className="mt-3 space-y-2">
      <FieldLabel>Éléments à classer — ordre correct (glissez pour réordonner)</FieldLabel>
      {items.map((item, index) => (
        <div
          key={`${index}-${item}`}
          draggable
          onDragStart={() => {
            dragIndex = index;
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            if (dragIndex != null) reorder(dragIndex, index);
            dragIndex = null;
          }}
          className="flex cursor-grab items-end gap-2 rounded-lg border border-dashed border-outline-variant/40 bg-surface-container-low px-3 py-2 text-sm active:cursor-grabbing"
        >
          <span className="mb-2 text-xs font-bold text-primary">{index + 1}.</span>
          <LabeledField label={`Libellé de l’élément ${index + 1}`} htmlFor={`rank-item-${index}`} className="min-w-0 flex-1">
            <input
              id={`rank-item-${index}`}
              className={fieldClass}
              value={item}
              onChange={(e) => {
                const next = [...items];
                next[index] = e.target.value;
                onChange(next);
              }}
            />
          </LabeledField>
        </div>
      ))}
      <button
        type="button"
        className="text-xs font-semibold text-primary underline"
        onClick={() => onChange([...items, `Élément ${items.length + 1}`])}
      >
        + Élément
      </button>
    </div>
  );
}

export default function ElearningExerciseEditor({ value, onChange }: Props) {
  const questions = value.length > 0 ? value : [newExerciseQuestion(0)];

  function updateQuestion(index: number, patch: Partial<ExerciseQuestion>) {
    const next = questions.map((q, i) => (i === index ? { ...q, ...patch } : q));
    onChange(next);
  }

  function updateOption(qIndex: number, oIndex: number, text: string) {
    const q = questions[qIndex];
    const options = [...(q.options ?? [])];
    const old = options[oIndex];
    options[oIndex] = text;
    const patch: Partial<ExerciseQuestion> = { options };
    if (q.reponseCorrecte === old) patch.reponseCorrecte = text;
    updateQuestion(qIndex, patch);
  }

  function changeType(index: number, type: ExerciseQuestionType) {
    updateQuestion(index, newExerciseQuestion(index, type));
  }

  return (
    <div className="space-y-4">
      {questions.map((q, qi) => (
        <fieldset key={q.id} className="rounded-xl border border-outline-variant/20 p-4">
          <legend className="px-1 text-sm font-bold text-primary">Question {qi + 1}</legend>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <LabeledField label="Type de question" htmlFor={`q-type-${q.id}`}>
              <select
                id={`q-type-${q.id}`}
                className={fieldClass}
                value={q.type ?? 'QCM'}
                onChange={(e) => changeType(qi, e.target.value as ExerciseQuestionType)}
              >
                {(Object.keys(EXERCISE_QUESTION_TYPE_LABELS) as ExerciseQuestionType[]).map((t) => (
                  <option key={t} value={t}>
                    {EXERCISE_QUESTION_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </LabeledField>
            <LabeledField label="Points" htmlFor={`q-points-${q.id}`}>
              <input
                id={`q-points-${q.id}`}
                className={fieldClass}
                type="number"
                min={1}
                value={q.points ?? 1}
                onChange={(e) => updateQuestion(qi, { points: parseInt(e.target.value, 10) || 1 })}
              />
            </LabeledField>
          </div>
          <LabeledField label="Énoncé de la question" htmlFor={`q-libelle-${q.id}`} className="mt-3">
            <textarea
              id={`q-libelle-${q.id}`}
              className={`${fieldClass} min-h-[4.5rem] resize-y`}
              rows={2}
              value={q.libelle}
              onChange={(e) => updateQuestion(qi, { libelle: e.target.value })}
            />
          </LabeledField>

          {(q.type ?? 'QCM') === 'QCM' ? (
            <div className="mt-3 space-y-3">
              <FieldLabel>Options de réponse — cochez la bonne réponse</FieldLabel>
              {(q.options ?? []).map((opt, oi) => (
                <div key={`${q.id}-${oi}`} className="flex items-end gap-2">
                  <label
                    className="mb-2 flex shrink-0 cursor-pointer items-center"
                    htmlFor={`q-opt-radio-${q.id}-${oi}`}
                    title="Bonne réponse"
                  >
                    <input
                      id={`q-opt-radio-${q.id}-${oi}`}
                      type="radio"
                      name={`correct-${q.id}`}
                      checked={q.reponseCorrecte === opt}
                      onChange={() => updateQuestion(qi, { reponseCorrecte: opt })}
                    />
                  </label>
                  <LabeledField
                    label={`Option ${oi + 1}`}
                    htmlFor={`q-opt-${q.id}-${oi}`}
                    className="min-w-0 flex-1"
                  >
                    <input
                      id={`q-opt-${q.id}-${oi}`}
                      className={fieldClass}
                      value={opt}
                      onChange={(e) => updateOption(qi, oi, e.target.value)}
                    />
                  </LabeledField>
                </div>
              ))}
              <button
                type="button"
                className="text-xs font-semibold text-primary underline"
                onClick={() =>
                  updateQuestion(qi, {
                    options: [...(q.options ?? []), String.fromCharCode(65 + (q.options?.length ?? 0))],
                  })
                }
              >
                + Ajouter une option
              </button>
            </div>
          ) : null}

          {q.type === 'VRAI_FAUX' ? (
            <fieldset className="mt-3">
              <legend className="formateur-notes-field__label mb-2">Bonne réponse</legend>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`vf-${q.id}`}
                    checked={q.reponseVraiFaux === true}
                    onChange={() => updateQuestion(qi, { reponseVraiFaux: true })}
                  />
                  Vrai
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`vf-${q.id}`}
                    checked={q.reponseVraiFaux === false}
                    onChange={() => updateQuestion(qi, { reponseVraiFaux: false })}
                  />
                  Faux
                </label>
              </div>
            </fieldset>
          ) : null}

          {q.type === 'TEXTE_LIBRE' ? (
            <LabeledField
              label="Réponse attendue (comparaison exacte, sans tenir compte des majuscules/accents)"
              htmlFor={`q-texte-${q.id}`}
              className="mt-3"
            >
              <input
                id={`q-texte-${q.id}`}
                className={fieldClass}
                value={q.reponseTexte ?? ''}
                onChange={(e) => updateQuestion(qi, { reponseTexte: e.target.value })}
              />
            </LabeledField>
          ) : null}

          {q.type === 'CLASSEMENT' ? (
            <RankingEditor
              items={q.ordreCorrect ?? q.items ?? []}
              onChange={(ordreCorrect) => updateQuestion(qi, { ordreCorrect, items: ordreCorrect })}
            />
          ) : null}
        </fieldset>
      ))}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="academic-panel-btn academic-panel-btn--secondary"
          onClick={() => onChange([...questions, newExerciseQuestion(questions.length)])}
        >
          Ajouter une question
        </button>
        {questions.length > 1 ? (
          <button
            type="button"
            className="academic-panel-btn academic-panel-btn--danger"
            onClick={() => onChange(questions.slice(0, -1))}
          >
            Retirer la dernière
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function exerciseQuestionsToJson(questions: ExerciseQuestion[]) {
  return { questions: questions.filter((q) => q.libelle.trim()) };
}

export function initExerciseReponses(questions: ExerciseQuestion[]): Record<string, unknown> {
  const initial: Record<string, unknown> = {};
  for (const q of questions) {
    if (q.type === 'CLASSEMENT') {
      initial[q.id] = [...(q.items ?? q.ordreCorrect ?? [])];
    }
  }
  return initial;
}

export function jsonToExerciseQuestions(raw: unknown): ExerciseQuestion[] {
  const questions = (raw as { questions?: ExerciseQuestion[] })?.questions;
  if (!Array.isArray(questions)) return [];
  return questions.map((q, i) => {
    const type = (q.type ?? 'QCM') as ExerciseQuestionType;
    const id = q.id || `q${i + 1}`;
    const base = { id, type, libelle: q.libelle || '', points: q.points ?? 1 };
    if (type === 'VRAI_FAUX') {
      return { ...base, reponseVraiFaux: q.reponseVraiFaux ?? q.reponseCorrecte === 'true' };
    }
    if (type === 'TEXTE_LIBRE') {
      return { ...base, reponseTexte: q.reponseTexte ?? q.reponseCorrecte ?? '' };
    }
    if (type === 'CLASSEMENT') {
      const items = Array.isArray(q.items) && q.items.length > 0 ? q.items : ['A', 'B', 'C'];
      return {
        ...base,
        items,
        ordreCorrect: Array.isArray(q.ordreCorrect) && q.ordreCorrect.length > 0 ? q.ordreCorrect : items,
      };
    }
    return {
      ...base,
      options: Array.isArray(q.options) && q.options.length > 0 ? q.options : ['A', 'B'],
      reponseCorrecte: q.reponseCorrecte || 'A',
    };
  });
}
